import { useLiveQuery } from "dexie-react-hooks";
import { DEFAULT_SHELF_CONFIG, type ShelfConfig } from "@recipevault/shared";
import { db } from "../../sync/db.js";

export interface UseShelfConfigResult {
  config: ShelfConfig | null;
  loading: boolean;
}

// Sentinel timestamp/author for the pre-sync placeholder below — mirrors the
// backend's own EPOCH_ISO synthesis in get-shelf-config.ts for "no row yet".
const PRE_SYNC_UPDATED_AT = "1970-01-01T00:00:00Z";

/**
 * Read-only view of the household's shelf-config document.
 *
 * `useLiveQuery` can't distinguish "hasn't resolved yet" from "resolved and
 * genuinely has no row" — both read as `undefined` from `db.shelfConfig.get`.
 * That's fine here: either way there's no synced config to show yet, so both
 * map to `loading: true` with a DEFAULT_SHELF_CONFIG-merged placeholder for
 * `config`, so callers never have to null-check before reading
 * order/hidden/custom. Once SyncService.syncShelfConfig() (bootstrap + the
 * background tick) writes the singleton row, the live query re-renders with
 * the real value and `loading` flips to false.
 */
export function useShelfConfig(): UseShelfConfigResult {
  const row = useLiveQuery(() => db.shelfConfig.get("config"));

  if (row === undefined) {
    return {
      config: {
        schemaVersion: 1,
        ...DEFAULT_SHELF_CONFIG,
        updatedAt: PRE_SYNC_UPDATED_AT,
        updatedBy: "",
      },
      loading: true,
    };
  }

  // ShelfConfigRow extends ShelfConfig (adds only `_singleton`), so the Dexie
  // row is already structurally a valid ShelfConfig — no stripping needed.
  return { config: row, loading: false };
}
